import React, { useEffect, useMemo, useState } from "react";
import { Button, Alert, Spinner, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { BillingClient, FileResponse, StartCheckoutDto } from "../web-api-client.ts";

type BillingStatus = "pending_setup" | "trialing" | "active" | "past_due" | "canceled";

async function fileResponseToJson<T>(fr: FileResponse): Promise<T> {
  const text = await fr.data.text();
  return JSON.parse(text) as T;
}

const Activate: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const billingClient = useMemo(() => new BillingClient(), []);

  async function fetchBillingStatus(): Promise<BillingStatus> {
    
    const res = await fetch("/api/Billing/status", { credentials: "include" });
    if (!res.ok) throw new Error("Unable to fetch billing status.");
    const data = await res.json();
    return data.status as BillingStatus;
  }

  async function startCheckout() {
    setError(null);
    setLoading(true);
    try {
      const dto: StartCheckoutDto = { dealerId: undefined as any }; // inferred server-side
      const fr = await billingClient.billing_StartCheckout(dto);
      const { sessionUrl } = await fileResponseToJson<{ sessionUrl: string }>(fr);
      window.location.href = sessionUrl;
    } catch (e: any) {
      setError(e.message ?? "Failed to start checkout.");
      setLoading(false);
    }
  }

  useEffect(() => {
    const autoStart = true;
    (async () => {
      try {
        const status = await fetchBillingStatus();
        setBillingStatus(status);
        if (status === "active" || status === "trialing") {
          navigate("/");
          return;
        }
        if (autoStart) await startCheckout();
        else setLoading(false);
      } catch (e: any) {
        setError(e.message ?? "Error while activating.");
        setLoading(false);
      }
    })();
  }, [navigate]);

  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
      <Card className="p-4" style={{ maxWidth: 520, width: "100%" }}>
        <h3 className="mb-3">Activate your account</h3>
        {loading && (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" role="status" />
            <div>Taking you to secure checkout…</div>
          </div>
        )}
        {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
        {!loading && (billingStatus === "pending_setup" || billingStatus === "past_due") && (
          <>
            <p className="text-muted">To start using QCAR, please complete your billing setup.</p>
            <Button onClick={startCheckout}>Complete Payment</Button>
          </>
        )}
      </Card>
    </div>
  );
};

export default Activate;
